import { getAccessToken } from '../authentication/supabaseService';
import { InventoryCount, InventoryGroup, InventorySection, InventorySectionProduct } from './inventoryService';

export interface ExpectedInventorySettings {
  sameStock?: Record<string, { fullCountGroupId: string; mode: 'included' | 'extra' }>;
  countDayOrders?: Record<string, { countedOrderIds: string[]; countDate: string }>;
}

export interface ExpectedInventoryMinimum {
  groupId: string;
  productKey: string;
  thresholdInStdUnit: number;
  minimum: number | null;
  countUnit: string;
  countUnitToStdFactor: number;
  stdUnit: string;
  purchaseUnitToStdFactor: number | null;
  supplier: { platform: string | null; name: string | null };
}

export interface ExpectedInventoryBootstrap {
  restaurantId: string;
  counts: InventoryCount[];
  groups: InventoryGroup[];
  sections: InventorySection[];
  products: InventorySectionProduct[];
  minimums: ExpectedInventoryMinimum[];
  settings: ExpectedInventorySettings;
  defaultWastePercentage: number;
  /** Inclusive/exclusive conversion by base key; frozen counts remain ex-VAT. */
  stockVatRatios?: Record<string, number>;
}

export type ExpectedInventorySettingsUpdate =
  | { type: 'sameStock'; groupId: string; value: { fullCountGroupId: string; mode: 'included' | 'extra' } | null }
  | { type: 'countDayOrders'; groupId: string; sessionId: string; value: { countedOrderIds: string[]; countDate: string } | null };

async function request(restaurantId: string, update?: ExpectedInventorySettingsUpdate): Promise<Response> {
  const token = await getAccessToken();
  const response = await fetch(`/api/inventory/expected/${encodeURIComponent(restaurantId)}${update ? '/settings' : ''}`, {
    method: update ? 'PATCH' : 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(update ? { body: JSON.stringify(update) } : {}),
  });
  if (!response.ok) throw new Error(`Expected inventory request failed (${response.status})`);
  return response;
}

export async function fetchExpectedInventory(restaurantId: string): Promise<ExpectedInventoryBootstrap> {
  const data: ExpectedInventoryBootstrap = await (await request(restaurantId)).json();
  return {
    ...data,
    counts: data.counts.map(count => ({ ...count, countedAt: new Date(count.countedAt) })),
    groups: data.groups.map(group => ({ ...group, createdAt: new Date(group.createdAt), updatedAt: new Date(group.updatedAt) })),
    sections: data.sections.map(section => ({ ...section, createdAt: new Date(section.createdAt), updatedAt: new Date(section.updatedAt) })),
    products: data.products.map(product => ({ ...product, createdAt: new Date(product.createdAt) })),
  };
}

export async function updateExpectedInventorySettings(restaurantId: string, update: ExpectedInventorySettingsUpdate): Promise<ExpectedInventorySettings> {
  const data: { settings: ExpectedInventorySettings } = await (await request(restaurantId, update)).json();
  return data.settings;
}
